import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, MessageCircle } from "lucide-react";
import { getProduct } from "@/lib/api";

const WHATSAPP_NUMBER = "19146730325";

export default function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await getProduct(id);
        setProduct(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12">
        <div className="animate-pulse grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="aspect-square rounded-[2rem] bg-sand" />
          <div className="space-y-4">
            <div className="h-8 bg-sand rounded w-1/3" />
            <div className="h-12 bg-sand rounded w-2/3" />
            <div className="h-24 bg-sand rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="py-32 text-center">
        <h2 className="font-heading text-3xl text-forest">Producto no encontrado</h2>
        <Link to="/catalogo" className="mt-4 inline-block text-terracotta font-body">Volver al catálogo</Link>
      </div>
    );
  }

  const whatsappMsg = encodeURIComponent(`Hola Oymer, me interesa el producto: ${product.name}. ¿Podrías darme más información y precio?`);

  return (
    <div data-testid="product-detail-page" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Link
          to="/catalogo"
          data-testid="back-to-catalog-link"
          className="inline-flex items-center gap-2 font-body text-sm text-sage hover:text-forest transition-colors duration-200 mb-8"
        >
          <ArrowLeft size={16} /> Volver al catálogo
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Image */}
          <div className="rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(42,74,60,0.08)]">
            <img
              src={product.image_url}
              alt={product.name}
              data-testid="product-detail-image"
              className="w-full aspect-square object-cover"
            />
          </div>

          {/* Info */}
          <div className="space-y-6">
            <span className="inline-block font-body text-xs font-semibold tracking-[0.15em] uppercase text-terracotta bg-terracotta/10 px-3 py-1 rounded-full">
              {product.category}
            </span>

            <h1 data-testid="product-detail-name" className="font-heading text-4xl md:text-5xl tracking-tight font-medium text-forest">
              {product.name}
            </h1>

            <p data-testid="product-detail-price" className="font-body text-xl font-semibold text-terracotta">
              {product.price ? `$${product.price.toFixed(2)}` : product.price_text}
            </p>

            <p data-testid="product-detail-description" className="font-body text-base md:text-lg leading-relaxed text-sage">
              {product.description}
            </p>

            {product.benefits && product.benefits.length > 0 && (
              <div>
                <h3 className="font-heading text-lg font-semibold text-forest mb-3">Beneficios</h3>
                <ul className="space-y-2">
                  {product.benefits.map((b, i) => (
                    <li key={i} data-testid={`benefit-item-${i}`} className="flex items-center gap-2 font-body text-sm text-sage">
                      <Check size={16} className="text-forest flex-shrink-0" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {product.ingredients && (
              <div>
                <h3 className="font-heading text-lg font-semibold text-forest mb-2">Ingredientes</h3>
                <p data-testid="product-detail-ingredients" className="font-body text-sm text-sage leading-relaxed">
                  {product.ingredients}
                </p>
              </div>
            )}

            <div className="flex flex-wrap gap-4 pt-4">
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMsg}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-whatsapp-button"
                className="bg-[#25D366] text-white rounded-full px-6 py-4 font-body font-medium flex items-center justify-center gap-2 transition-transform duration-300 hover:scale-105 shadow-[0_8px_20px_rgba(37,211,102,0.25)]"
              >
                <MessageCircle size={20} strokeWidth={1.5} /> Pedir por WhatsApp
              </a>
              <Link
                to="/contacto"
                data-testid="product-contact-button"
                className="bg-forest text-white rounded-full px-6 py-4 font-body font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_20px_rgba(42,74,60,0.15)]"
              >
                Formulario de Pedido
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
